import ApiService from '../../../../services/apiService';
import moment from 'moment';
import { mapState } from 'vuex';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import cloneDeep from 'lodash/cloneDeep';
import pullAt from 'lodash/pullAt';
import get from 'lodash/get';
import { userItemsPerListMixin } from '@/utils/vuex';
const defaultItemsPerPage = 10;
const defaultTableOptions = {
  sortBy: ['dtwin.title'],
  sortDesc: [false],
  page: 1,
  itemsPerPage: defaultItemsPerPage
};
export default {
  name: 'CoreDtwinRuleOverviewTable',
  mixins: [userItemsPerListMixin],
  props: {
    tenantId: {
      type: Number,
      required: true
    },
    dtwinId: {
      type: Number,
      required: true
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    hasTwinEditPermission: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data: function () {
    return {
      bridgeTypes: [],
      bridgeInstances: [],
      rulesList: [],
      tableOptions: defaultTableOptions,
      priorRequestOptions: null,
      serverItemsLength: 0,
      searchTerm: null,
      deletionSelection: [],
      deleteDialog: false,
      deletionInProgress: false,
      targetRuleIndex: -1,
      disableFetchOnCreatedMixin: true,
      includeVDRules: false,
      isFetchingCommandExecutions: false
    };
  },
  computed: {
    ...mapState('app', ['inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme']),
    detailButtonIcon() {
      return this.hasTwinEditPermission ? 'fa-edit' : 'fa-eye';
    },
    tableLocalized() {
      return {
        footerProps: {
          itemsPerPageText: this.$i18n_t('Common.rowsPerPage'),
          itemsPerPageOptions: [defaultItemsPerPage, 20, 50]
        },
        headers: [{
          sortable: false
        }, {
          sortable: true,
          text: this.$i18n_t('DigitalTwin.DigitalTwin.states.title'),
          value: 'title'
        }, {
          sortable: false
        }, {
          sortable: true,
          text: this.$i18n_t('Common.digitalTwins.searchLabel'),
          value: 'dtwin.title'
        }, {
          value: 'geoFences',
          align: 'end',
          sortable: false
        }, {
          value: 'timeTriggers',
          align: 'start',
          sortable: false
        }, {
          sortable: true,
          text: this.$i18n_t('DigitalTwin.DigitalTwin.ruleOverviewTable.lastTriggered'),
          value: 'lastTrigger'
        }, {
          sortable: false,
          text: this.$i18n_t('DigitalTwin.DigitalTwin.ruleOverviewTable.lastActions'),
          value: 'properties.actions'
        }]
      };
    }
  },
  watch: {
    tableOptions: {
      handler() {
        this.fetchRuleList();
      },
      deep: true
    },
    dtwinId() {
      this.priorRequestOptions = null;
      this.tableOptions = cloneDeep(defaultTableOptions);
    },
    searchTerm: debounce(function () {
      this.fetchRuleList();
    }, 400, {
      leading: true
    }),
    includeVDRules() {
      this.tableOptions = {
        ...this.tableOptions,
        page: 1
      };
    }
  },
  created() {
    this.tableOptions.itemsPerPage = this.savedUserItemsPerList();
  },
  mounted() {
    this.fetchTwinRules();
  },
  methods: {
    get,
    fetchTwinRules() {
      let bridgeTypesPromise = this.hasTwinEditPermission ? ApiService.getBridgeTypes().then(bridgeTypes => {
        return bridgeTypes.filter(type => {
          // only display bridges that have instance commands
          return Object.keys(get(type, 'manifest.commands.instance', {})).length > 0;
        });
      }) : Promise.resolve([]);
      let tenantBridgePromise = this.hasTwinEditPermission ? ApiService.getBridgesForTenant(this.tenantId) : Promise.resolve([]);
      return Promise.all([bridgeTypesPromise, tenantBridgePromise, this.fetchRuleList(true)]).then(results => {
        this.bridgeTypes = results[0];
        this.bridgeInstances = results[1];
        this.rulesList = get(results, '[2].rules');
        this.serverItemsLength = get(results, '[2].totalCount');
      });
    },
    fetchRuleList(returnOnly = false) {
      const searchTerm = (this.searchTerm || '').trim().length > 0 ? this.searchTerm.trim() : undefined;
      if (get(this.priorRequestOptions, 'searchTerm') !== searchTerm) {
        // if the search-item is changed, reset to page 1
        this.tableOptions = {
          ...this.tableOptions,
          page: 1
        };
      }
      const {
        sortBy,
        sortDesc,
        page,
        itemsPerPage
      } = this.tableOptions;
      const sortItem = sortBy && sortBy.length ? sortBy[0] : 'dtwin.title';
      const doSortDesc = sortDesc && sortDesc.length ? sortDesc[0] : true;
      const sortDirection = doSortDesc ? 'DESC' : 'ASC';
      const usedPage = Math.max(1, page || 1);
      const usedItemsPerPage = itemsPerPage || defaultItemsPerPage;
      const offset = (usedPage - 1) * usedItemsPerPage;
      const newRequestOptions = {
        offset,
        limit: usedItemsPerPage,
        order: {
          variable: sortItem,
          direction: sortDirection
        },
        searchTerm,
        includeVDRules: this.includeVDRules
      };
      if (isEqual(newRequestOptions, this.priorRequestOptions)) {
        return Promise.resolve({});
      }
      this.priorRequestOptions = newRequestOptions;
      return ApiService.digitalTwinFetchRulesOverviewList(this, this.dtwinId, newRequestOptions.offset, newRequestOptions.limit, newRequestOptions.order, newRequestOptions.searchTerm, this.includeVDRules).then(results => {
        if (returnOnly) {
          return results;
        }
        this.rulesList = results.rules;
        this.serverItemsLength = results.totalCount;
        return results;
      });
    },
    // fetch the command-executions on-demand one after another
    async fetchCommandExecutions() {
      this.isFetchingCommandExecutions = true;
      for (let i = 0; i < this.rulesList.length; i++) {
        await ApiService.digitalTwinFetchRulesExecutionsOverviewList(this, this.dtwinId, this.rulesList[i].id).then(results => {
          // it may be the case that the user switched pages and the current fetched result is not valid any more
          if (get(this.rulesList, `[${i}].properties.actions`)) {
            this.rulesList[i].properties.actions = results;
            this.rulesList[i] = cloneDeep(this.rulesList[i]);
          }
        }).catch(error => {
          console.error(error);
        });
      }
      this.isFetchingCommandExecutions = false;
    },
    deleteRuleToggled(item) {
      if (this.deletionSelection.findIndex(i => i.id === item.id) >= 0) {
        this.deletionSelection = this.deletionSelection.filter(i => i.id !== item.id);
      } else {
        const selection = cloneDeep(this.deletionSelection);
        selection.push(item);
        this.deletionSelection = selection;
      }
    },
    deleteRule(index) {
      if (this.rulesList[index].id) {
        this.deletionInProgress = true;
        ApiService.digitalTwinDeleteRule(this, this.dtwinId, this.rulesList[index].id).then(() => {
          this.deleteDialog = false;
          this.targetRuleIndex = -1;
          pullAt(this.rulesList, [index]);
          this.rulesList = cloneDeep(this.rulesList);
          this.priorRequestOptions = null;
          this.tableOptions = {
            ...defaultTableOptions,
            random: new Date()
          };
        }).catch(error => {
          if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
            this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
          } else {
            this.$snack(get(error, 'response.data.message'), this.$i18n_t('Error.unknown'), 'error');
          }
        }).finally(() => {
          this.deletionInProgress = false;
        });
      }
    },
    deleteSelectedRules() {
      this.deletionInProgress = true;
      Promise.all(this.deletionSelection.map(item => {
        return ApiService.digitalTwinDeleteRule(this, this.dtwinId, item.id);
      })).then(() => {
        this.deleteDialog = false;
        this.deletionSelection = [];
        this.targetRuleIndex = -1;
        this.priorRequestOptions = null;
        this.tableOptions = {
          ...defaultTableOptions,
          random: new Date()
        };
      }).catch(error => {
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(get(error, 'response.data.message'), this.$i18n_t('Error.unknown'), 'error');
        }
      }).finally(() => {
        this.deletionInProgress = false;
      });
    },
    processedRuleActions(rule) {
      return (get(rule, 'properties.actions') || []).map(action => this.processRuleAction(action));
    },
    processRuleAction(action) {
      const commandParts = (get(action, 'command') || '').split('.');
      if (commandParts.length < 2) {
        return {};
      }
      const bridgeInstanceId = commandParts[0];
      const command = action.command.substring(bridgeInstanceId.length + 1);
      const triggerType = action.triggerType === 'CHANGES' ? this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler.changes') : this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler.evaluates');
      const bridgeInstance = this.bridgeInstances.find(b => b.id === parseInt(bridgeInstanceId));
      return {
        command: `${get(bridgeInstance, 'name') || 'Unknown'}.${command}`,
        condition: `${this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler.ifCondition')} ${triggerType} ${this.$i18n_t('DigitalTwin.DigitalTwin.ruleModeler.conditionTo')} ${action.triggerValue}`,
        lastExecution: action.lastExecution ? this.localizeDate(get(action, 'lastExecution.date') || null) : null
      };
    },
    localizeDate(date) {
      if (date) {
        moment.locale(this.$store.state.app.language || window.navigator.userLanguage || window.navigator.language);
        return moment(date).format('L LTS');
      }
      return this.$i18n_t('Common.noData');
    },
    updatePagination(e) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          ...{
            page: e.page || defaultTableOptions.page
          },
          ...{
            perPage: e.itemsPerPage || defaultTableOptions.itemsPerPage
          }
        }
      }).catch(error => {
        console.error(error);
      });
    },
    baseDetailLink(item) {
      const basePath = item.dtwin.type === 'virtualDevice' ? 'virtual-devices' : 'digitaltwin-mgmt';
      return `/${basePath}/${item.dtwinId}`;
    },
    detailButtonClicked(item) {
      this.$router.push(`${this.baseDetailLink(item)}?ruleId=${item.id}`, '_blank');
    }
  }
};